import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SEO from "@/components/seo/SEO";
import { Eyebrow, SectionHeading, AmbientOrbs } from "@/components/common/ui";
import { API } from "@/lib/api";
import { CheckCircle, WarningCircle, Hourglass, ArrowClockwise } from "@phosphor-icons/react";

const STORAGE = "bht_admin_token";

const STATUS_STYLES = {
  sent: { icon: CheckCircle, cls: "border-emerald-500/30 bg-emerald-500/5 text-emerald-300" },
  failed: { icon: WarningCircle, cls: "border-red-500/30 bg-red-500/5 text-red-300" },
  retrying: { icon: Hourglass, cls: "border-bht-gold/40 bg-bht-gold/10 text-bht-gold" },
  queued: { icon: Hourglass, cls: "border-white/10 bg-white/[0.03] text-white/60" },
  pending_config: { icon: WarningCircle, cls: "border-yellow-500/30 bg-yellow-500/5 text-yellow-300" },
};

export default function AdminEmails() {
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE) || "");
  const [inputToken, setInputToken] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (t) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/emails/log?limit=200`, { headers: { "X-Admin-Token": t } });
      if (res.status === 401) {
        setError("Invalid admin token.");
        setData(null);
        setLoading(false);
        return;
      }
      const j = await res.json();
      setData(j);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  const submitToken = (e) => {
    e.preventDefault();
    if (!inputToken.trim()) return;
    localStorage.setItem(STORAGE, inputToken.trim());
    setToken(inputToken.trim());
  };

  const logout = () => {
    localStorage.removeItem(STORAGE);
    setToken("");
    setData(null);
  };

  return (
    <>
      <SEO title="Admin — Email log" path="/admin/emails" />
      <section className="relative overflow-hidden py-24 lg:py-32">
        <AmbientOrbs />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <Eyebrow>Admin · Deliverability</Eyebrow>
          <SectionHeading
            className="mt-4"
            title={lang === "nl" ? "Email deliverability dashboard" : "Email deliverability dashboard"}
            sub={lang === "nl"
              ? "Elke verzonden email — status, aantal pogingen, provider-id en fout — op één plek."
              : "Every email sent — status, attempts, provider id and error — in one place."}
          />
        </div>
      </section>

      <section className="relative border-t border-white/[0.06] pb-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          {!token ? (
            <form onSubmit={submitToken} className="mx-auto max-w-md rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8" data-testid="admin-token-form">
              <div className="eyebrow mb-3">Admin token</div>
              <input
                type="password"
                autoComplete="off"
                value={inputToken}
                onChange={(e) => setInputToken(e.target.value)}
                placeholder="paste admin token…"
                className="w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-white placeholder:text-white/30 focus:border-bht-gold/60 focus:outline-none"
                data-testid="admin-token-input"
              />
              <button
                type="submit"
                className="mt-4 w-full rounded-full bg-bht-gold px-5 py-3 text-[12px] font-semibold uppercase tracking-widest text-bht-void hover:scale-[1.02]"
                data-testid="admin-token-submit"
              >
                Unlock
              </button>
              {error && <div className="mt-3 text-xs text-red-300">{error}</div>}
              <p className="mt-4 text-xs text-white/40">
                Set the ADMIN_TOKEN env var on the backend; the same value unlocks this dashboard.
              </p>
            </form>
          ) : (
            <>
              <div className="mb-8 flex items-center justify-between">
                <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="admin-summary">
                  {["total", "sent", "failed", "retrying", "pending_config"].map((k) => (
                    <div key={k} className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3">
                      <div className="eyebrow">{k.replace("_", " ")}</div>
                      <div className="mt-2 font-serif text-2xl">{(data?.summary?.[k]) ?? 0}</div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => load(token)} className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-[11px] uppercase tracking-widest text-white/70 hover:border-bht-gold/50 hover:text-bht-gold" data-testid="admin-refresh">
                    <ArrowClockwise size={12} /> Refresh
                  </button>
                  <button onClick={logout} className="text-[11px] uppercase tracking-widest text-white/40 hover:text-white">Logout</button>
                </div>
              </div>

              <div className="overflow-hidden rounded-2xl border border-white/[0.06]" data-testid="admin-log-table">
                <div className="grid grid-cols-[110px_1fr_1fr_120px_60px_1fr] gap-4 border-b border-white/[0.06] bg-white/[0.02] px-5 py-3 text-[10px] font-mono uppercase tracking-widest text-white/40">
                  <div>Status</div>
                  <div>Kind</div>
                  <div>To</div>
                  <div>Created</div>
                  <div>Try</div>
                  <div>Provider / error</div>
                </div>
                {loading && <div className="px-5 py-6 text-white/40">Loading…</div>}
                {!loading && data && data.logs.map((l) => {
                  const st = STATUS_STYLES[l.status] || STATUS_STYLES.queued;
                  const Icon = st.icon;
                  return (
                    <div key={l.id} className={`grid grid-cols-[110px_1fr_1fr_120px_60px_1fr] items-center gap-4 border-b border-white/[0.04] px-5 py-3 text-sm last:border-b-0`} data-testid={`admin-log-row-${l.id}`}>
                      <div className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${st.cls}`}>
                        <Icon size={10} weight="fill" />
                        {l.status}
                      </div>
                      <div className="text-xs text-white/80">{l.kind}</div>
                      <div className="text-xs text-white/70">{l.to}</div>
                      <div className="font-mono text-[10px] text-white/40">{new Date(l.created_at).toLocaleString(lang)}</div>
                      <div className="font-mono text-[10px] text-white/60">{l.attempts}</div>
                      <div className="truncate text-[11px] text-white/50">
                        {l.provider_id ? <span className="text-white/70">{l.provider_id}</span> : l.error ? <span className="text-red-300">{l.error}</span> : "—"}
                      </div>
                    </div>
                  );
                })}
                {!loading && data && data.logs.length === 0 && (
                  <div className="px-5 py-10 text-center text-white/40">No emails yet.</div>
                )}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
